import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@headlessui/react";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { ReactSortable } from "react-sortablejs";
import {
  fetchLoyaltyProgram,
  resequenceLoyaltyEvents,
} from "../../../apiService/loyalty.service";
import { SORTABLE_OPTIONS } from "../constants";
import { getApiErrorMessage, mapApiEventForReorder } from "../utils";

export default function EventsOrderModal({ isOpen, programId, programTitle, onClose }) {
  const [events, setEvents] = useState([]);
  const [originalIds, setOriginalIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !programId) return undefined;

    let cancelled = false;

    const loadEvents = async () => {
      setIsLoading(true);
      setError("");
      setEvents([]);
      setOriginalIds([]);
      try {
        const program = await fetchLoyaltyProgram(programId);
        if (cancelled) return;
        const apiEvents = Array.isArray(program?.loyalty_events) ? program.loyalty_events : [];
        const mapped = apiEvents.map(mapApiEventForReorder);
        setEvents(mapped);
        setOriginalIds(mapped.map((item) => item.eventId).filter(Boolean));
      } catch (loadError) {
        if (!cancelled) {
          setError(getApiErrorMessage(loadError, "Failed to load events."));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [isOpen, programId]);

  const eventIds = useMemo(
    () => events.map((event) => event.eventId).filter(Boolean),
    [events]
  );
  const hasOrderChanged =
    eventIds.length !== originalIds.length ||
    eventIds.some((id, index) => String(id) !== String(originalIds[index]));

  const handleSave = async () => {
    if (!programId || !eventIds.length || isSaving) return;
    setIsSaving(true);
    setError("");
    try {
      await resequenceLoyaltyEvents(programId, eventIds);
      onClose();
    } catch (saveError) {
      setError(getApiErrorMessage(saveError, "Failed to save event order."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={isSaving ? () => {} : onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-bold text-gray-900">Events</Dialog.Title>
          <p className="mt-1 text-sm text-gray-600">
            Drag to reorder events{programTitle ? ` for ${programTitle}` : ""}.
          </p>

          {isLoading ? <p className="mt-6 text-sm text-gray-500">Loading events...</p> : null}
          {!isLoading && error ? <p className="mt-6 text-sm text-red-600">{error}</p> : null}
          {!isLoading && !error && events.length === 0 ? (
            <p className="mt-6 text-sm text-gray-500">No events found for this program.</p>
          ) : null}
          {!isLoading && events.length > 0 ? (
            <div className="mt-5 space-y-2">
              <div className="grid grid-cols-[2rem_1fr_1fr] gap-3 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span />
                <span>Title (EN)</span>
                <span>Title (HI)</span>
              </div>
              <ReactSortable
                list={events}
                setList={setEvents}
                {...SORTABLE_OPTIONS}
                className="space-y-2"
              >
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="grid grid-cols-[2rem_1fr_1fr] items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-3"
                  >
                    <span
                      className="drag-handle inline-flex cursor-grab items-center justify-center text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                      aria-label="Drag to reorder"
                    >
                      <Bars3Icon className="h-5 w-5" />
                    </span>
                    <p className="text-sm font-medium text-gray-900">{event.titleEn || "—"}</p>
                    <p className="text-sm text-gray-700">{event.titleHi || "—"}</p>
                  </div>
                ))}
              </ReactSortable>
            </div>
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600 disabled:cursor-not-allowed disabled:bg-cyan-300"
              onClick={handleSave}
              disabled={isLoading || isSaving || !hasOrderChanged || eventIds.length === 0}
            >
              {isSaving ? "Saving..." : "Save change"}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
